import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Instagram, Facebook, Send, Sparkles, Lock, RefreshCw, Loader2,
  Search, UserPlus, Link2, X, ExternalLink, MoreVertical, Paperclip, Smile,
  ChevronDown, Star, Phone, Mail, Calendar, User, Archive, Trash2, Check, CheckCheck,
  Circle, ArrowLeft,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { locale } from "@/lib/format";
import { MessageList } from "@/components/whatsapp/Conversation";
import { UPLOAD_ERRORS } from "@/components/WhatsAppChat";

/**
 * Unified Inbox — WhatsApp + Instagram + Facebook DM in un'unica UI.
 * Layout 3 colonne stile Workhubs con:
 *  - Sfondi laterali leggermente grigi per far spiccare la chat centrale
 *  - Preferiti + unread + archiviati (state persistito in inbox_thread_state)
 *  - Colori canale fedeli al brand originale
 *  - Menu ⋮ con azioni: segna letto/non letto, archivia, elimina
 */
export default function Inbox() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("all"); // all|whatsapp|instagram|facebook|favorites|archived
  const [linking, setLinking] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [ctxFilter, setCtxFilter] = useState("all"); // all|lead|customer
  const [leadInboxCap, setLeadInboxCap] = useState(false);
  const [dmEnabled, setDmEnabled] = useState(false);

  useEffect(() => {
    api.get("/meta/pages/config")
      .then((r) => setDmEnabled(!!r.data?.dm_enabled))
      .catch(() => setDmEnabled(false));
  }, []);

  useEffect(() => {
    api.get("/agency/tenant-context")
      .then((r) => setLeadInboxCap(!!r.data?.capabilities?.agency_lead_inbox))
      .catch(() => {});
  }, []);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (channelFilter === "archived") params.set("include_archived", "true");
      if (channelFilter === "favorites") params.set("only_favorites", "true");
      if (ctxFilter !== "all") params.set("conversation_context", ctxFilter);
      const r = await api.get(`/whatsapp/threads?${params.toString()}`);
      setThreads(r.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [channelFilter, ctxFilter]);

  const identifyThread = (t) => ({
    channel: t.channel,
    client_id: t.client_id || null,
    peer_id: t.channel === "instagram" ? t.ig_sender_id
           : t.channel === "facebook" ? t.fb_sender_id : null,
  });

  const loadThread = useCallback(async (t) => {
    setActive(t);
    setMessages([]);
    // Auto mark-as-read quando si apre un thread non letto
    if (t.unread) {
      try {
        await api.post("/inbox/threads/mark-read", identifyThread(t));
        setThreads((old) => old.map((x) => x === t ? { ...x, unread: false } : x));
      } catch { /* ignore */ }
    }
    try {
      if (t.channel === "instagram") {
        const r = await api.get(`/meta/ig/messages/${t.ig_sender_id}`);
        setMessages(r.data.messages || []);
      } else if (t.channel === "facebook") {
        const r = await api.get(`/meta/fb/messages/${t.fb_sender_id}`);
        setMessages(r.data.messages || []);
      } else if (t.client_id) {
        const r = await api.get(`/whatsapp/messages/${t.client_id}`);
        setMessages(r.data.messages || []);
      }
    } catch { /* empty */ }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // Deep-link dalla Pipeline: ?client=<id> → apre la chat del contatto.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const want = searchParams.get("client");
    if (!want || active || !threads.length) return;
    const t = threads.find((x) => x.client_id === want);
    if (t) { loadThread(t); searchParams.delete("client"); setSearchParams(searchParams, { replace: true }); }
  }, [searchParams, setSearchParams, threads, active, loadThread]);

  const filteredThreads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return threads.filter((t) => {
      const ch = t.channel || "whatsapp";
      if (!dmEnabled && (ch === "instagram" || ch === "facebook")) return false;
      if (["whatsapp", "instagram", "facebook"].includes(channelFilter) &&
          ch !== channelFilter) return false;
      if (!q) return true;
      const hay = `${t.client_name || ""} ${t.client_phone || ""} ${t.ig_sender_id || ""} ${t.fb_sender_id || ""} ${t.last_message || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [threads, search, channelFilter, dmEnabled]);

  const unreadCount = useMemo(() => threads.filter((t) => t.unread).length, [threads]);
  const favoriteCount = useMemo(() => threads.filter((t) => t.favorite).length, [threads]);

  /* --- SSE realtime: refresh threads + active thread on any new message --- */
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) return;
    const token = localStorage.getItem("sp_token");
    if (!token) return;
    const url = `${backendUrl}/api/whatsapp/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    es.addEventListener("message", () => {
      loadThreads();
      if (active) loadThread(active);
    });
    es.onerror = () => { /* auto-reconnect handled by browser */ };
    return () => es.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.client_id, active?.ig_sender_id]);

  const sendFile = async (f) => {
    if (!active?.client_id) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("client_id", active.client_id);
      fd.append("caption", reply.trim());
      fd.append("file", f);
      const res = await api.post("/whatsapp/send-media", fd);
      if (res.data?.status === "error") toast.error(t("inbox.attachmentRejected"));
      else { setReply(""); toast.success(t("inbox.attachmentSent")); }
      loadThread(active);
      loadThreads();
    } catch (e) {
      toast.error(t(UPLOAD_ERRORS[e?.response?.data?.detail] || "inbox.attachmentError"));
    }
    setUploading(false);
  };

  const send = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      if (active.channel === "instagram") {
        await api.post("/meta/ig/reply", { ig_sender_id: active.ig_sender_id, text: reply.trim() });
      } else if (active.channel === "facebook") {
        await api.post("/meta/fb/reply", { psid: active.fb_sender_id, text: reply.trim() });
      } else {
        if (!active.window_open) {
          toast.error(t("inbox.windowClosedToast"));
          setSending(false);
          return;
        }
        await api.post("/whatsapp/send", { client_id: active.client_id, body: reply.trim() });
      }
      setReply("");
      toast.success(t("inbox.sent"));
      loadThread(active);
      loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("inbox.sendError"));
    }
    setSending(false);
  };

  const toggleStar = async (t, e) => {
    e?.stopPropagation();
    try {
      const r = await api.post("/inbox/threads/star", identifyThread(t));
      setThreads((old) => old.map((x) => x === t ? { ...x, favorite: r.data.favorite } : x));
      if (active === t) setActive({ ...t, favorite: r.data.favorite });
    } catch { toast.error("Errore preferiti"); }
  };

  const markAs = async (t, read) => {
    try {
      await api.post(`/inbox/threads/${read ? "mark-read" : "mark-unread"}`, identifyThread(t));
      setThreads((old) => old.map((x) => x === t ? { ...x, unread: !read } : x));
      toast.success(read ? "Segnato come letto" : "Segnato come non letto");
    } catch { toast.error("Errore"); }
  };

  const archive = async (t) => {
    try {
      const r = await api.post("/inbox/threads/archive", identifyThread(t));
      toast.success(r.data.archived ? "Conversazione archiviata" : "Ripristinata dall'archivio");
      if (active === t) setActive(null);
      loadThreads();
    } catch { toast.error("Errore"); }
  };

  const deleteThread = async (t) => {
    if (!window.confirm(`Eliminare TUTTI i messaggi con "${t.client_name}"? Operazione irreversibile.`)) return;
    try {
      await api.delete("/inbox/threads", { data: identifyThread(t) });
      toast.success("Conversazione eliminata");
      if (active === t) setActive(null);
      loadThreads();
    } catch { toast.error("Errore"); }
  };

  return (
    <div data-testid="inbox-page" className="flex flex-col flex-1 min-h-0">
      <header className="flex items-center justify-between flex-shrink-0 mb-4">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tight flex items-center gap-3">
            <MessageSquare className="h-8 w-8 text-[color:var(--sp-brand)]" /> {t("inbox.title")}
            {unreadCount > 0 && (
              <span className="text-sm font-bold px-2.5 py-0.5 rounded-full bg-emerald-500 text-white" data-testid="inbox-unread-count">
                {unreadCount}
              </span>
            )}
          </h1>
          <p className="mt-1 text-sm text-[color:var(--sp-text-secondary)]">
            {dmEnabled ? t("inbox.subtitleDm") : t("inbox.subtitle")}
          </p>
        </div>
        <button
          onClick={loadThreads}
          className="text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)]"
          data-testid="inbox-refresh-btn"
        >
          <RefreshCw className="h-3.5 w-3.5" /> {t("common.refresh")}
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_280px] gap-4 flex-1 min-h-0">
        {/* ============ Colonna 1: Thread list (sfondo grigio) ============ */}
        <div className={`rounded-2xl border border-[color:var(--sp-border)] bg-slate-50 overflow-hidden flex-col min-h-0 ${active ? "hidden lg:flex" : "flex"}`}>
          <div className="p-4 border-b border-[color:var(--sp-border)] space-y-3 bg-slate-50">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-base font-black tracking-tight inline-flex items-center gap-1">
                {t("inbox.allMessages")} <ChevronDown className="h-3.5 w-3.5 text-[color:var(--sp-text-muted)]" />
              </h2>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]" data-testid="inbox-thread-count">
                {filteredThreads.length}
              </span>
            </div>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("inbox.searchPlaceholder")}
                data-testid="inbox-search-input"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-full bg-white border border-transparent focus:outline-none focus:border-[color:var(--sp-brand)]"
              />
            </div>
            <div className="flex gap-1.5 flex-wrap" data-testid="inbox-channel-filter">
              {[
                { key: "all", label: t("inbox.filterAll") },
                { key: "favorites", label: `${t("inbox.filterFavorites")}${favoriteCount > 0 ? ` (${favoriteCount})` : ""}`, icon: Star },
                { key: "whatsapp", label: "WA", icon: MessageSquare },
                ...(dmEnabled ? [
                  { key: "instagram", label: "IG", icon: Instagram },
                  { key: "facebook", label: "FB", icon: Facebook },
                ] : []),
                { key: "archived", label: t("inbox.filterArchived"), icon: Archive },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setChannelFilter(f.key)}
                  data-testid={`inbox-filter-${f.key}`}
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 transition-colors ${
                    channelFilter === f.key
                      ? "bg-[color:var(--sp-brand)] text-white"
                      : "bg-white border border-[color:var(--sp-border)] text-[color:var(--sp-text-secondary)] hover:bg-slate-100"
                  }`}
                >
                  {f.icon && <f.icon className="h-3 w-3" />}{f.label}
                </button>
              ))}
            </div>
          </div>

          {leadInboxCap && (
            <div className="flex items-center gap-1.5 px-3 pb-2" data-testid="inbox-context-filters">
              {[
                { key: "all", label: t("inbox.ctxAll") },
                { key: "lead", label: t("inbox.ctxLead") },
                { key: "customer", label: t("inbox.ctxCustomer") },
              ].map((f) => (
                <button key={f.key} onClick={() => setCtxFilter(f.key)}
                  data-testid={`inbox-ctx-${f.key}`}
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors ${
                    ctxFilter === f.key
                      ? "bg-[color:var(--sp-brand)] text-white"
                      : "bg-white border border-[color:var(--sp-border)] text-[color:var(--sp-text-secondary)] hover:bg-slate-100"
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="p-4 text-sm text-[color:var(--sp-text-secondary)] flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Carico…
            </div>
          ) : filteredThreads.length === 0 ? (
            <div className="p-6 text-center text-sm text-[color:var(--sp-text-muted)]">
              {threads.length === 0 ? "Nessun messaggio ricevuto ancora." :
               channelFilter === "favorites" ? "Nessun thread preferito. Clicca la stella per aggiungerne." :
               channelFilter === "archived" ? "Nessuna conversazione archiviata." :
               "Nessun risultato per la ricerca."}
            </div>
          ) : (
            <div className="overflow-y-auto flex-1 min-h-0" data-testid="inbox-threads">
              {filteredThreads.map((t, i) => {
                const tid = `${t.channel}-${t.client_id || t.ig_sender_id || t.fb_sender_id || i}`;
                const isActive = active && (
                  (t.client_id && t.client_id === active.client_id) ||
                  (t.ig_sender_id && t.ig_sender_id === active.ig_sender_id) ||
                  (t.fb_sender_id && t.fb_sender_id === active.fb_sender_id)
                );
                return (
                  <ThreadRow key={tid} testId={tid} t={t} active={isActive}
                             onClick={() => loadThread(t)}
                             onToggleStar={(e) => toggleStar(t, e)} />
                );
              })}
            </div>
          )}
        </div>

        {/* ============ Colonna 2: Conversazione (bianca, in evidenza) ============ */}
        <div className={`rounded-2xl border border-[color:var(--sp-border)] bg-white shadow-lg shadow-slate-200/60 overflow-hidden flex-col min-h-0 ${active ? "flex" : "hidden lg:flex"}`}>
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8 text-center">
              <div className="h-20 w-20 rounded-full bg-[color:var(--sp-brand)]/10 flex items-center justify-center">
                <MessageSquare className="h-10 w-10 text-[color:var(--sp-brand)]" />
              </div>
              <div>
                <div className="font-display text-lg font-black">{t("inbox.noConversation")}</div>
                <div className="text-sm text-[color:var(--sp-text-muted)] mt-1">
                  {t("inbox.chooseThread")}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="px-4 lg:px-5 py-3 border-b border-[color:var(--sp-border)] flex items-center gap-2 lg:gap-3 relative flex-shrink-0">
                <button
                  onClick={() => setActive(null)}
                  className="lg:hidden p-1.5 -ml-1 rounded-full hover:bg-slate-100"
                  title={t("inbox.backToConversations")}
                  data-testid="inbox-back-btn"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <Avatar name={active.client_name} channel={active.channel} />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate flex items-center gap-2">
                    {active.client_name || t("inbox.noName")}
                    <ChannelBadge channel={active.channel} windowOpen={active.window_open} />
                  </div>
                  <div className="text-[11px] text-[color:var(--sp-text-muted)] truncate">
                    {active.client_phone || active.ig_sender_id || active.fb_sender_id || "—"}
                  </div>
                </div>
                <button
                  onClick={(e) => toggleStar(active, e)}
                  className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                  title={active.favorite ? t("inbox.unstar") : t("inbox.star")}
                  data-testid="inbox-star-btn"
                >
                  <Star className={`h-4 w-4 ${active.favorite ? "fill-amber-400 text-amber-400" : "text-[color:var(--sp-text-muted)]"}`} />
                </button>
                <button
                  onClick={() => setShowMenu((v) => !v)}
                  className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                  title={t("inbox.moreActions")}
                  data-testid="inbox-more-btn"
                >
                  <MoreVertical className="h-4 w-4 text-[color:var(--sp-text-muted)]" />
                </button>
                {showMenu && (
                  <ThreadActionMenu
                    thread={active}
                    onClose={() => setShowMenu(false)}
                    onMarkRead={() => { setShowMenu(false); markAs(active, true); }}
                    onMarkUnread={() => { setShowMenu(false); markAs(active, false); }}
                    onArchive={() => { setShowMenu(false); archive(active); }}
                    onDelete={() => { setShowMenu(false); deleteThread(active); }}
                  />
                )}
              </div>

              <MessageList messages={messages} testId="inbox-messages" emptyText={t("inbox.noMessages")} />

              <div className="px-4 py-3 border-t border-[color:var(--sp-border)] bg-white flex-shrink-0">
                <div className="flex items-end gap-2 rounded-2xl bg-slate-100 px-3 py-2">
                  <button type="button" className="p-1.5 rounded-full hover:bg-white text-[color:var(--sp-text-muted)]" title="Emoji" data-testid="inbox-emoji-btn">
                    <Smile className="h-4 w-4" />
                  </button>
                  <input
                    type="file" ref={fileRef} className="hidden" data-testid="inbox-file-input"
                    accept="image/jpeg,image/png,video/mp4,video/3gpp,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) sendFile(f); }}
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={active.channel !== "whatsapp" || !active.window_open || uploading}
                    className="p-1.5 rounded-full hover:bg-white text-[color:var(--sp-text-muted)] disabled:opacity-40 disabled:cursor-not-allowed"
                    title={active.channel !== "whatsapp"
                      ? t("inbox.attachWhatsappOnly")
                      : active.window_open
                        ? t("inbox.attachTooltip")
                        : t("inbox.attachWindowClosed")}
                    data-testid="inbox-attach-btn"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                  </button>
                  <textarea
                    data-testid="inbox-composer"
                    className="flex-1 bg-transparent border-none focus:outline-none text-sm resize-none py-1 min-h-[24px] max-h-24"
                    placeholder={
                      active.channel === "instagram" ? t("inbox.replyInstagram") :
                      active.channel === "facebook" ? t("inbox.replyFacebook") :
                      (active.window_open ? t("inbox.writeMessage") : t("inbox.windowClosed"))
                    }
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    rows={1}
                    disabled={active.channel === "whatsapp" && !active.window_open}
                  />
                  <button
                    data-testid="inbox-send-btn"
                    onClick={send}
                    disabled={sending || !reply.trim() || (active.channel === "whatsapp" && !active.window_open)}
                    className="h-8 w-8 rounded-full bg-[color:var(--sp-brand)] text-white flex items-center justify-center hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                    title={t("common.send")}
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ============ Colonna 3: Dettagli contatto (sfondo grigio) ============ */}
        <div className="rounded-2xl border border-[color:var(--sp-border)] bg-slate-50 overflow-hidden hidden lg:flex flex-col min-h-0">
          {!active ? (
            <div className="flex-1 flex items-center justify-center p-6 text-center text-sm text-[color:var(--sp-text-muted)]">
              {t("inbox.contactWillAppear")}
            </div>
          ) : (
            <ContactDetailsPanel thread={active} onLink={() => setLinking(true)} nav={nav} />
          )}
        </div>
      </div>

      {linking && active && (
        <LinkContactModal
          thread={active}
          onClose={() => setLinking(false)}
          onLinked={() => { setLinking(false); loadThreads(); loadThread(active); }}
        />
      )}
    </div>
  );
}

/* ============================================================
   Thread action menu (⋮)
============================================================ */
function ThreadActionMenu({ thread, onClose, onMarkRead, onMarkUnread, onArchive, onDelete }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, [onClose]);
  return (
    <div ref={ref}
         data-testid="inbox-action-menu"
         className="absolute top-full right-4 mt-1 z-30 w-52 bg-white rounded-xl border border-[color:var(--sp-border)] shadow-lg py-1 text-sm">
      {thread.unread ? (
        <MenuItem onClick={onMarkRead} icon={CheckCheck} label="Segna come letto" tid="inbox-menu-mark-read" />
      ) : (
        <MenuItem onClick={onMarkUnread} icon={Circle} label={t("inbox.markUnread")} tid="inbox-menu-mark-unread" />
      )}
      <MenuItem onClick={onArchive} icon={Archive}
                label={thread.archived ? t("inbox.unarchive") : t("inbox.archive")}
                tid="inbox-menu-archive" />
      <div className="h-px bg-[color:var(--sp-border)] my-1" />
      <MenuItem onClick={onDelete} icon={Trash2} label={t("inbox.deleteConversation")}
                tid="inbox-menu-delete" tone="danger" />
    </div>
  );
}

function MenuItem({ onClick, icon: Icon, label, tid, tone }) {
  const cls = tone === "danger"
    ? "text-[color:var(--sp-danger)] hover:bg-rose-50"
    : "text-[color:var(--sp-text)] hover:bg-slate-50";
  return (
    <button
      data-testid={tid}
      onClick={onClick}
      className={`w-full text-left px-3 py-2 flex items-center gap-2 ${cls}`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

/* ============================================================
   Thread list row
============================================================ */
function ThreadRow({ t, active, onClick, onToggleStar, testId }) {
  const { t: tr } = useTranslation();
  const unread = t.unread;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      className={`w-full text-left px-4 py-3 border-b border-[color:var(--sp-border)]/70 flex gap-3 items-center transition-colors cursor-pointer ${
        active
          ? "bg-white border-l-2 border-l-[color:var(--sp-brand)]"
          : "border-l-2 border-l-transparent hover:bg-white/60"
      }`}
      data-testid={`inbox-thread-${testId}`}
      data-channel={t.channel}
      data-active={active ? "true" : "false"}
      data-unread={unread ? "true" : "false"}
      data-favorite={t.favorite ? "true" : "false"}
    >
      <Avatar name={t.client_name} channel={t.channel} size="md" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className={`text-sm truncate ${unread ? "font-black" : "font-bold"}`} data-testid={`inbox-thread-name-${testId}`}>
            {t.client_name || tr("inbox.noName")}
          </div>
          <div className={`text-[10px] flex-shrink-0 font-medium ${unread ? "text-emerald-600 font-bold" : "text-[color:var(--sp-text-muted)]"}`}>
            {formatTime(t.last_timestamp)}
          </div>
        </div>
        <div className={`text-xs truncate mt-0.5 flex items-center gap-1 ${unread ? "text-[color:var(--sp-text)] font-bold" : "text-[color:var(--sp-text-secondary)]"}`} data-testid={`inbox-thread-preview-${testId}`}>
          {t.last_direction === "out" && <span className="text-[color:var(--sp-text-muted)] font-normal">{tr("inbox.you")}:</span>}
          <span className="truncate">{t.last_message || "…"}</span>
        </div>
      </div>
      {/* Star + unread dot */}
      <div className="flex flex-col items-center gap-1 flex-shrink-0">
        <button
          type="button"
          onClick={onToggleStar}
          className="p-1 rounded-full hover:bg-slate-200 transition-colors"
          data-testid={`inbox-thread-star-${testId}`}
          title={t.favorite ? tr("inbox.unstar") : tr("inbox.star")}
        >
          <Star className={`h-3.5 w-3.5 ${t.favorite ? "fill-amber-400 text-amber-400" : "text-[color:var(--sp-text-muted)]"}`} />
        </button>
        {unread && (
          <span className="h-2 w-2 rounded-full bg-emerald-500" data-testid={`inbox-thread-unread-dot-${testId}`} />
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Avatar — colori ufficiali dei canali
============================================================ */
function Avatar({ name, channel, size = "md" }) {
  const cls = size === "lg" ? "h-14 w-14 text-lg" : size === "sm" ? "h-8 w-8 text-xs" : "h-11 w-11 text-sm";
  const initials = (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";
  // Colori canale fedeli ai brand originali
  const style =
    channel === "instagram"
      ? { background: "linear-gradient(135deg, #833AB4 0%, #FD1D1D 50%, #FCB045 100%)" }
      : channel === "facebook"
        ? { background: "#1877F2" }
        : { background: "#25D366" }; // WhatsApp Green ufficiale
  return (
    <div className="relative flex-shrink-0">
      <div className={`${cls} rounded-full text-white font-black flex items-center justify-center shadow-sm`} style={style}>
        {initials}
      </div>
      <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-white flex items-center justify-center shadow">
        <ChannelDot channel={channel} />
      </div>
    </div>
  );
}

function ChannelDot({ channel }) {
  if (channel === "instagram") return <Instagram className="h-2.5 w-2.5" style={{ color: "#E4405F" }} />;
  if (channel === "facebook") return <Facebook className="h-2.5 w-2.5" style={{ color: "#1877F2" }} />;
  return <MessageSquare className="h-2.5 w-2.5" style={{ color: "#25D366" }} />;
}

/* ============================================================
   Channel badge (pill)
============================================================ */
function ChannelBadge({ channel, windowOpen }) {
  if (channel === "instagram") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#FEE7EF", color: "#E4405F" }}>
        <Instagram className="h-2.5 w-2.5" /> Instagram
      </span>
    );
  }
  if (channel === "facebook") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#E7F0FE", color: "#1877F2" }}>
        <Facebook className="h-2.5 w-2.5" /> Facebook
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={windowOpen ? { background: "#D8F5E4", color: "#128C4E" } : { background: "#FEF3C7", color: "#B45309" }}>
      {windowOpen ? (<><Sparkles className="h-2.5 w-2.5" /> 24h</>) : (<><Lock className="h-2.5 w-2.5" /> Template</>)}
    </span>
  );
}

/* ============================================================
   Contact details right column
============================================================ */
function ContactDetailsPanel({ thread, onLink, nav }) {
  const { t } = useTranslation();
  const [client, setClient] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setClient(null); setStats(null);
    if (!thread?.client_id) return;
    setLoading(true);
    api.get(`/clients/${thread.client_id}/history`)
      .then((r) => {
        if (!alive) return;
        setClient(r.data.client);
        setStats(r.data.stats);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [thread?.client_id]);

  const goToClient = () => {
    if (client) nav(`/app/clients/${client.id}`);
  };

  return (
    <div className="flex flex-col h-full">
      <div
        className={`px-5 py-4 border-b border-[color:var(--sp-border)] flex flex-col items-center text-center gap-2 ${client ? "cursor-pointer hover:bg-slate-100 transition-colors" : ""}`}
        onClick={client ? goToClient : undefined}
        data-testid="inbox-contact-header"
        role={client ? "button" : undefined}
      >
        <Avatar name={thread.client_name} channel={thread.channel} size="lg" />
        <div>
          <div className="font-display text-base font-black tracking-tight">{thread.client_name || t("inbox.noName")}</div>
          <div className="mt-1"><ChannelBadge channel={thread.channel} windowOpen={thread.window_open} /></div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
        {!thread.client_id ? (
          <div className="rounded-xl border border-dashed border-[color:var(--sp-border)] p-4 text-center space-y-2 bg-white">
            <div className="text-sm text-[color:var(--sp-text-secondary)]">
              {t("inbox.notLinked")}
            </div>
            <button
              onClick={onLink}
              data-testid="inbox-link-btn"
              className="sp-btn-primary inline-flex items-center gap-2 text-xs !py-1.5 !px-3"
            >
              <UserPlus className="h-3.5 w-3.5" /> {t("inbox.linkContact")}
            </button>
          </div>
        ) : loading ? (
          <div className="text-sm text-[color:var(--sp-text-muted)] flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("inbox.loadingProfile")}
          </div>
        ) : client ? (
          <>
            <div className="space-y-2">
              <div className="label-eyebrow">{t("inbox.contactDetails")}</div>
              {client.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-3.5 w-3.5 text-[color:var(--sp-text-muted)]" />
                  <span className="truncate">{client.phone}</span>
                </div>
              )}
              {client.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-3.5 w-3.5 text-[color:var(--sp-text-muted)]" />
                  <span className="truncate">{client.email}</span>
                </div>
              )}
              {!client.phone && !client.email && (
                <div className="text-xs text-[color:var(--sp-text-muted)]">{t("inbox.noContacts")}</div>
              )}
            </div>

            {stats && (
              <div className="space-y-2">
                <div className="label-eyebrow">{t("inbox.stats")}</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-white border border-[color:var(--sp-border)] px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--sp-text-muted)] font-bold">{t("inbox.appointments")}</div>
                    <div className="font-black text-lg tabular-nums" data-testid="inbox-contact-appts">{stats.total_appointments ?? 0}</div>
                  </div>
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">{t("inbox.spent")}</div>
                    <div className="font-black text-lg tabular-nums text-emerald-700" data-testid="inbox-contact-spent">
                      € {(stats.total_spent ?? 0).toLocaleString(locale(), { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                </div>
                {stats.last_appointment && (
                  <div className="flex items-center gap-2 text-xs text-[color:var(--sp-text-secondary)]">
                    <Calendar className="h-3.5 w-3.5" /> {t("inbox.lastVisit")}: {new Date(stats.last_appointment).toLocaleDateString(locale())}
                  </div>
                )}
              </div>
            )}

            <Link
              to={`/app/clients/${client.id}`}
              data-testid="inbox-open-client"
              className="w-full inline-flex items-center justify-center gap-2 text-xs font-bold px-3 py-2 rounded-lg border border-[color:var(--sp-border)] bg-white hover:bg-slate-100 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" /> {t("inbox.openFullProfile")}
            </Link>
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm text-[color:var(--sp-text-muted)]">
            <User className="h-3.5 w-3.5" /> Nessun dettaglio disponibile
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Time helper
============================================================ */
function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString(locale(), { day: "2-digit", month: "short" });
  } catch { return iso; }
}

/* ============================================================
   Link contact modal
============================================================ */
function LinkContactModal({ thread, onClose, onLinked }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("search");
  const [q, setQ] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [newName, setNewName] = useState(thread.client_name || "");
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const peerId = thread.channel === "instagram" ? thread.ig_sender_id
               : thread.channel === "facebook" ? thread.fb_sender_id
               : null;

  const doSearch = async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (phone) params.set("phone", phone);
      if (email) params.set("email", email);
      if (q) params.set("q", q);
      const r = await api.get(`/inbox/match?${params.toString()}`);
      setMatches(r.data.matches || []);
    } catch (e) {
      toast.error("Errore ricerca");
    } finally { setSearching(false); }
  };

  const linkTo = async (clientId) => {
    setBusy(true);
    try {
      await api.post("/inbox/threads/link", {
        channel: thread.channel,
        peer_id: peerId,
        client_id: clientId,
      });
      toast.success("Contatto collegato");
      onLinked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore link");
    } finally { setBusy(false); }
  };

  const createAndLink = async () => {
    if (!newName.trim()) return toast.error("Il nome è obbligatorio");
    setBusy(true);
    try {
      const r = await api.post("/inbox/threads/create-client", {
        channel: thread.channel,
        peer_id: peerId,
        name: newName.trim(),
        phone: newPhone.trim() || null,
        email: newEmail.trim() || null,
        source: thread.channel,
      });
      toast.success(`Contatto ${r.data.client.name} creato e collegato`);
      onLinked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose} data-testid="inbox-link-modal">
      <div className="sp-card w-full max-w-lg p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-black">{t("inbox.linkContact")}</h3>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <div className="flex gap-1 mb-4">
          <button onClick={() => setTab("search")} data-testid="link-tab-search"
                  className={`flex-1 text-xs font-bold px-3 py-2 rounded-lg ${tab === "search" ? "bg-[color:var(--sp-brand)] text-white" : "bg-[color:var(--sp-subtle)]"}`}>
            <Link2 className="inline h-3 w-3 mr-1" /> Cerca esistente
          </button>
          <button onClick={() => setTab("create")} data-testid="link-tab-create"
                  className={`flex-1 text-xs font-bold px-3 py-2 rounded-lg ${tab === "create" ? "bg-[color:var(--sp-brand)] text-white" : "bg-[color:var(--sp-subtle)]"}`}>
            <UserPlus className="inline h-3 w-3 mr-1" /> Crea nuovo
          </button>
        </div>

        {tab === "search" && (
          <div className="space-y-3">
            <p className="text-xs text-[color:var(--sp-text-muted)]">
              Ricerca per numero o email (match esatto), oppure per nome/parte di contatto.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="+39…" value={phone} onChange={(e) => setPhone(e.target.value)}
                className="sp-input text-sm" data-testid="link-search-phone" />
              <input placeholder="email@…" value={email} onChange={(e) => setEmail(e.target.value)}
                className="sp-input text-sm" data-testid="link-search-email" />
            </div>
            <input placeholder="Nome (fuzzy)" value={q} onChange={(e) => setQ(e.target.value)}
              className="sp-input text-sm" data-testid="link-search-name" />
            <button onClick={doSearch} disabled={searching} data-testid="link-search-btn"
              className="sp-btn-primary w-full inline-flex items-center justify-center gap-2">
              <Search className="h-3.5 w-3.5" /> {searching ? "Cerco…" : "Cerca"}
            </button>
            {matches.length > 0 && (
              <ul className="space-y-1 max-h-60 overflow-y-auto" data-testid="link-matches">
                {matches.map((m) => (
                  <li key={m.id}>
                    <button onClick={() => linkTo(m.id)} disabled={busy}
                      data-testid={`link-match-${m.id}`}
                      className="w-full text-left p-2 rounded-lg border border-[color:var(--sp-border)] hover:border-[color:var(--sp-brand)] hover:bg-[color:var(--sp-subtle)]">
                      <div className="font-bold text-sm">{m.name}</div>
                      <div className="text-[11px] text-[color:var(--sp-text-muted)]">
                        {m.email && <span>{m.email}</span>}
                        {m.email && m.phone && <span> · </span>}
                        {m.phone && <span>{m.phone}</span>}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {matches.length === 0 && !searching && (phone || email || q) && (
              <div className="text-xs text-[color:var(--sp-text-muted)] text-center">
                Nessun match. Prova a creare un nuovo contatto.
              </div>
            )}
          </div>
        )}

        {tab === "create" && (
          <div className="space-y-3">
            <p className="text-xs text-[color:var(--sp-text-muted)]">
              Crea un nuovo contatto CRM e collega automaticamente la conversazione {thread.channel}.
            </p>
            <div>
              <label className="text-xs font-bold">Nome *</label>
              <input value={newName} onChange={(e) => setNewName(e.target.value)}
                className="sp-input mt-1" data-testid="link-create-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold">Telefono</label>
                <input value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
                  placeholder="+39…" className="sp-input mt-1" data-testid="link-create-phone" />
              </div>
              <div>
                <label className="text-xs font-bold">Email</label>
                <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                  className="sp-input mt-1" data-testid="link-create-email" />
              </div>
            </div>
            <button onClick={createAndLink} disabled={busy} data-testid="link-create-btn"
              className="sp-btn-primary w-full">
              {busy ? "Creo…" : "Crea e collega"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
